/**
 * @fileoverview Кастомный React хук для получения геолокации с отказоустойчивыми механизмами.
 * Реализует двухэтапную стратегию: Telegram LocationManager → Browser Geolocation API.
 * Включает специальную обработку для iOS в Telegram WebApp и детальное логирование.
 * 
 * @since 1.0.0
 */

import { locationManager } from '@telegram-apps/sdk';
import { useCallback, useEffect, useState } from 'react';
import { logger } from '../../../utils/logger';

/**
 * Координаты геолокации в десятичных градусах.
 * 
 * @since 1.0.0
 */
export interface GeolocationType {
    /** Широта в диапазоне от -90 до 90 градусов */
    latitude: number;
    /** Долгота в диапазоне от -180 до 180 градусов */
    longitude: number;
}

/**
 * Статус получения геолокации.
 * 
 * @since 1.0.0
 */
export type GeolocationStatus = 'loading' | 'success' | 'error';

/**
 * Состояние, возвращаемое хуком useGeolocation.
 * 
 * @since 1.0.0
 */
interface GeolocationState {
    /** Полученные координаты или null если недоступны */
    location: GeolocationType | null;
    /** Текст ошибки для отображения пользователю или null при успехе */
    error: string | null;
    /** Текущий статус процесса получения геолокации */
    status: GeolocationStatus;
    /** true если геолокация в процессе получения */
    isLoading: boolean;
    /** true если Telegram LocationManager поддерживается */
    isSupported: boolean;
    /** true если Telegram LocationManager успешно инициализирован */
    isMounted: boolean;
    /** Функция для повторного запроса геолокации */
    retry: () => void;
}

const TELEGRAM_TIMEOUT = 5000;         // 5 секунд для Telegram LocationManager
const BROWSER_FALLBACK_TIMEOUT = 5000; // 5 секунд для Browser fallback
const TOTAL_GEOLOCATION_TIMEOUT = 10000; // Общий лимит 10 секунд

/**
 * Проверяет, являются ли координаты валидными местоположением.
 * 
 * @param location - Объект с координатами или null
 * 
 * @returns true если координаты валидны
 * 
 * @remarks
 * Проверяет:
 * - Наличие объекта location
 * - Что latitude и longitude являются числами
 * - Координаты в допустимых диапазонах (-90 ≤ lat ≤ 90, -180 ≤ lng ≤ 180)
 * - Не является значением по умолчанию (0, 0)
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * isValidLocation({ latitude: 55.7558, longitude: 37.6176 }); // true
 * isValidLocation({ latitude: 0, longitude: 0 }); // false
 * isValidLocation(null); // false
 * ```
 */
const isValidLocation = (location: GeolocationType | null): boolean => {
    return !!(location && 
             typeof location.latitude === 'number' && 
             typeof location.longitude === 'number' &&
             Math.abs(location.latitude) <= 90 && 
             Math.abs(location.longitude) <= 180 &&
             !(location.latitude === 0 && location.longitude === 0));
};

/**
 * Определяет, работает ли приложение на iOS в Telegram Web App.
 * 
 * @returns true для iOS в Telegram WebApp среде
 * 
 * @remarks
 * Необходимо для специальной обработки геолокации на iOS.
 * На этой платформе могут возникать проблемы с таймаутами
 * геолокационных запросов.
 * 
 * Проверяет:
 * - iOS устройство (iPhone, iPad, iPod или Mac с сенсорным экраном)
 * - Telegram WebApp среду через глобальные объекты или User Agent
 * - Standalone режим (для PWA)
 * 
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * if (isIOSInTelegramWebApp()) {
 *   // Используем специальную логику для iOS
 * }
 * ```
 */
const isIOSInTelegramWebApp = (): boolean => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Проверяем либо standalone, либо Telegram WebApp UserAgent
    const isTelegramWebApp = (window as any).Telegram?.WebApp !== undefined ||
                             navigator.userAgent.includes('TelegramWebApp');
    
    return isIOS && (isTelegramWebApp || (navigator as any).standalone === true);
};

/**
 * Кастомный React хук для получения геолокации с отказоустойчивыми механизмами.
 * 
 * Реализует двухэтапную стратегию получения геолокации:
 * 1. **Telegram LocationManager** (5 сек) - приоритетный метод через Telegram SDK
 * 2. **Browser Geolocation API** (5 сек) - fallback через стандартный браузерный API
 * 
 * Включает специальные оптимизации для iOS в Telegram WebApp, где могут
 * возникать проблемы с таймаутами геолокационных запросов.
 * 
 * @param delayed - Если true, инициализация геолокации будет отложена до вызова retry()
 * 
 * @returns Объект состояния с координатами, статусом, ошибками и функцией повтора
 * 
 * @remarks
 * **Архитектурные особенности:**
 * - Защита от множественных одновременных запросов
 * - Детальное логирование для мониторинга
 * - Валидация координат против значений по умолчанию
 * - Автоматическая очистка ресурсов при размонтировании
 * 
 * **Таймауты:**
 * - Telegram API: 5 секунд
 * - Browser API: 5 секунд
 * - Общий лимит: 10 секунд
 * 
 * **Delayed режим:**
 * Используется в WebAppCam для задержки запроса геолокации до получения
 * доступа к камере. Это улучшает UX за счет последовательности запросов
 * разрешений и обходит баг react-webcam.
 * 
 * @throws Никогда не выбрасывает исключения - все ошибки обрабатываются внутри
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * // Обычное использование
 * const { location, error, status, retry } = useGeolocation();
 * 
 * // С задержкой (используется в WebAppCam)
 * const { location, error, status, retry } = useGeolocation(true);
 * 
 * // Обработка состояний
 * if (status === 'loading') return <div>Получение геолокации...</div>;
 * if (status === 'error') return <div>Ошибка: {error}</div>;
 * if (location) {
 *   console.log(`Координаты: ${location.latitude}, ${location.longitude}`);
 * }
 * ```
 * 
 * @see {@link GeolocationType} для формата координат
 * @see {@link GeolocationStatus} для возможных статусов
 */
export const useGeolocation = (delayed: boolean = false): GeolocationState => {
    const [status, setStatus] = useState<GeolocationStatus>('loading');
    const [location, setLocation] = useState<GeolocationType | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSupported, setIsSupported] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false); // Защита от множественных запросов
    const [telegramError, setTelegramError] = useState<string | null>(null);
    const [browserError, setBrowserError] = useState<string | null>(null);
    const [finalErrorMessage, setFinalErrorMessage] = useState<string | null>(null);



    const requestGeolocation = useCallback(async () => {
        if (isRequesting) {
            return;
        }
        
        setIsRequesting(true);
        setStatus('loading');
        setLocation(null);
        setError(null);
        setTelegramError(null);
        setBrowserError(null);
        setFinalErrorMessage(null);

        try {
            // ФАЗА 1: Telegram LocationManager с 5-сек таймаутом
            const isTelegramReady = locationManager.isSupported() && locationManager.isMounted();
            
            if (isTelegramReady) {
                try {
                    const telegramPromise = locationManager.requestLocation();
                    const telegramTimeoutPromise = new Promise<never>((_, reject) => {
                        setTimeout(() => reject(new Error('TELEGRAM_TIMEOUT')), TELEGRAM_TIMEOUT);
                    });
                    
                    const telegramLocation = await Promise.race([telegramPromise, telegramTimeoutPromise]);
                    
                    if (isValidLocation(telegramLocation)) {
                        setLocation(telegramLocation);
                        setStatus('success');
                        setIsRequesting(false);
                        return; // Успешный выход
                    }
                } catch (telegramError) {
                    if ((telegramError as Error).message === 'TELEGRAM_TIMEOUT') {
                        setTelegramError('таймаут');
                    } else {
                        setTelegramError((telegramError as Error).message);
                        logger.warn('Ошибка получения геолокации в Telegram LocationManager', { error: telegramError });
                    }
                }
            }
            
            // ФАЗА 2: Браузерный API с оставшимися 5 секундами
            await tryBrowserGeolocationWithTimeout(BROWSER_FALLBACK_TIMEOUT);
            
        } catch (error) {
            logger.error('Ошибка получения геолокации в WebView', { error });
            setStatus('error');
        } finally {
            setIsRequesting(false);
        }
    }, [isMounted, isSupported, isRequesting]);

    // Проверка разрешений на геолокацию
    const checkGeolocationPermission = async (): Promise<boolean> => {
        if (!navigator.permissions) {
            // Старые браузеры без Permissions API
            return true; // Попробуем сразу запросить геолокацию
        }
        
        try {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            return permission.state === 'granted' || permission.state === 'prompt';
        } catch (e) {
            logger.warn('Ошибка проверки разрешений геолокации', { error: e });
            return true; // Попробуем запросить в любом случае
        }
    };

    // Вспомогательная функция для обработки стандартных ошибок
    const handleGeolocationError = (error: GeolocationPositionError) => {
        let errorMessage = 'неизвестная ошибка';
        
        switch (error.code) {
            case error.PERMISSION_DENIED:
                errorMessage = 'доступ запрещён';
                break;
            case error.POSITION_UNAVAILABLE:
                errorMessage = 'местоположение недоступно';
                break;
            case error.TIMEOUT:
                errorMessage = 'таймаут';
                break;
        }
        
        setBrowserError(errorMessage);
        logger.error('Ошибка браузерного API геолокации', { error: errorMessage });
    };

    // Fallback на браузерный API геолокации с настраиваемым таймаутом
    const tryBrowserGeolocationWithTimeout = async (timeoutMs: number) => {
        if (!navigator.geolocation) {
            setBrowserError('геолокация не поддерживается');
            setStatus('error');
            setIsRequesting(false);
            throw new Error('геолокация не поддерживается');
        }

        const hasPermission = await checkGeolocationPermission();
        if (!hasPermission) {
            setBrowserError('доступ к геолокации не предоставлен');
            setStatus('error');
            setIsRequesting(false);
            throw new Error('доступ к геолокации не предоставлен');
        }
        
        const geolocationOptions = {
            enableHighAccuracy: true,
            timeout: timeoutMs, // Используем переданный таймаут (5 сек)
            maximumAge: 300000
        };

        // Для iOS в Telegram WebApp - всегда используем Promise.race
        if (isIOSInTelegramWebApp()) {            
            const promises = [
                // Принудительный таймаут (точно такой же как переданный)
                new Promise<never>((_, reject) => {
                    setTimeout(() => reject(new Error('IOS_BROWSER_TIMEOUT')), timeoutMs);
                }),
                
                // Основной геолокационный промис
                new Promise<GeolocationPosition>((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, geolocationOptions);
                })
            ];
            
            try {
                const position = await Promise.race(promises);
                setLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                });
                setStatus('success');
                setIsRequesting(false);
                return;
            } catch (error) {
                if ((error as Error).message === 'IOS_BROWSER_TIMEOUT') {
                    setBrowserError('таймаут');
                } else {
                    handleGeolocationError(error as GeolocationPositionError);
                }
                setStatus('error');
                setIsRequesting(false);
                throw error;
            }
        }
        
        // Стандартная логика для других платформ
        return new Promise<void>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });
                    setStatus('success');
                    setIsRequesting(false);
                    resolve();
                },
                (error) => {
                    handleGeolocationError(error);
                    setStatus('error');
                    setIsRequesting(false);
                    reject(error);
                },
                geolocationOptions
            );
        });
    };

    // Инициализация LocationManager
    useEffect(() => {
        // Если задержка активна - не инициализируем
        if (delayed) {
            return;
        }
        
        const initLocationManager = async () => {
            setStatus('loading');
            setError(null);
            
            // Проверяем поддержку геолокации в Telegram
            const isLocationSupported = locationManager.isSupported();
            
            if (isLocationSupported) {
                setIsSupported(true);
                try {
                    await locationManager.mount();
                    setIsMounted(true);
                    
                    // Даем время для обновления состояния React перед запросом
                    setTimeout(() => {
                        requestGeolocation();
                    }, 100);
                } catch (e) {
                    logger.warn('Не удалось инициализировать Telegram LocationManager', { error: e });
                    setIsSupported(false);
                    // Fallback на браузерный API если mount не удался
                    requestGeolocation();
                }
            } else {
                setIsSupported(false);
                // Сразу используем браузерный API если Telegram SDK не поддерживается
                requestGeolocation();
            }
        };

        initLocationManager();

        // Cleanup функция для размонтирования
        return () => {
            if (locationManager.isMounted()) {
                locationManager.unmount();
                setIsMounted(false);
            }
        };
    }, [delayed]);

    // useEffect для обновления итогового сообщения при изменении ошибок
    useEffect(() => {
        if (!telegramError && !browserError) {
            setFinalErrorMessage(null);
            return;
        }
        
        const baseMessage = 'Не удалось получить местоположение. Пожалуйста, проверьте, что: в системе включена служба геолокации, у Telegram есть доступ к геолокации, в профиле чат-бота включен есть доступ к геолокации.';
        
        const errorDetails: string[] = [];
        if (browserError) {
            errorDetails.push(browserError);
        }
        if (telegramError) {
            errorDetails.push(telegramError);
        }
        
        const finalMessage = errorDetails.length > 0 
            ? `${baseMessage} (${errorDetails.join(', ')})`
            : baseMessage;
            
        setFinalErrorMessage(finalMessage);
    }, [telegramError, browserError]);

    return {
        location,
        error: finalErrorMessage,
        status,
        isLoading: status === 'loading',
        isSupported,
        isMounted,
        retry: requestGeolocation,
    };
}; 
